"use client";

// Section "Infinite Grid" — bloc interactif placé juste avant le Footer.
// Deux couches de grille SVG superposées : une faible toujours visible, une
// nette révélée par un masque radial qui suit le curseur. Le motif dérive
// lentement en X/Y pour donner la sensation d'une grille sans fin. Au
// centre : appel à l'action minimal qui pousse vers le formulaire de
// contact.

import {
  motion,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
} from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

const CELL = 44;
const DRIFT_X = 0.18; // px/frame — slow horizontal pan
const DRIFT_Y = 0.12; // px/frame — slow vertical pan

function GridLayer({
  ox,
  oy,
  className,
}: {
  ox: ReturnType<typeof useMotionValue<number>>;
  oy: ReturnType<typeof useMotionValue<number>>;
  className?: string;
}) {
  return (
    <svg className={`h-full w-full ${className ?? ""}`} aria-hidden>
      <defs>
        <motion.pattern
          id="kssm-grid"
          width={CELL}
          height={CELL}
          patternUnits="userSpaceOnUse"
          x={ox}
          y={oy}
        >
          <path
            d={`M ${CELL} 0 L 0 0 0 ${CELL}`}
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
          />
        </motion.pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#kssm-grid)" />
    </svg>
  );
}

export function InfiniteGrid() {
  const t = useTranslations("infiniteGrid");

  const px = useMotionValue(-9999);
  const py = useMotionValue(-9999);
  const ox = useMotionValue(0);
  const oy = useMotionValue(0);

  // Sur mobile (pointer coarse), l'utilisateur n'a pas de souris : on anime
  // le centre du masque sur une trajectoire circulaire automatique pour que
  // l'effet "grille révélée" reste vivant.
  const [isCoarse, setIsCoarse] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const mql = window.matchMedia("(pointer: coarse)");
    const update = () => setIsCoarse(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  useAnimationFrame((time) => {
    ox.set((ox.get() + DRIFT_X) % CELL);
    oy.set((oy.get() + DRIFT_Y) % CELL);

    if (isCoarse && sectionRef.current) {
      const r = sectionRef.current.getBoundingClientRect();
      const t = time / 1000;
      // Trajectoire en lemniscate (∞) douce, ~12s par cycle.
      const cx = r.width / 2 + Math.sin(t * 0.5) * (r.width * 0.32);
      const cy = r.height / 2 + Math.sin(t * 1.0) * (r.height * 0.28);
      px.set(cx);
      py.set(cy);
    }
  });

  const mask = useMotionTemplate`radial-gradient(280px circle at ${px}px ${py}px, black 0%, transparent 70%)`;

  return (
    <section
      ref={sectionRef}
      onMouseMove={(e) => {
        if (isCoarse) return;
        const r = e.currentTarget.getBoundingClientRect();
        px.set(e.clientX - r.left);
        py.set(e.clientY - r.top);
      }}
      onMouseLeave={() => {
        if (isCoarse) return;
        px.set(-9999);
        py.set(-9999);
      }}
      className="relative isolate overflow-hidden py-20 sm:py-28 lg:py-36"
      data-no-divider
    >
      {/* Soft ambient blobs to give the grid a tinted backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-[-12%] top-[-15%] size-[44%] rounded-full bg-aurora-cyan/15 blur-[140px]" />
        <div className="absolute left-[-10%] bottom-[-18%] size-[42%] rounded-full bg-aurora-violet/15 blur-[140px]" />
      </div>

      {/* Faint always-on grid */}
      <div className="pointer-events-none absolute inset-0 text-foreground/15 opacity-50">
        <GridLayer ox={ox} oy={oy} />
      </div>

      {/* Bright cyan grid revealed under the cursor */}
      <motion.div
        className="pointer-events-none absolute inset-0 text-aurora-cyan opacity-90"
        style={{ maskImage: mask, WebkitMaskImage: mask }}
      >
        <GridLayer ox={ox} oy={oy} />
      </motion.div>

      {/* Centred call-out */}
      <div className="pointer-events-none relative mx-auto flex max-w-3xl flex-col items-center gap-6 px-4 text-center sm:px-6">
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          {t("eyebrow")}
        </span>
        <h2 className="font-headline text-[clamp(2.25rem,5.5vw,4.5rem)] font-medium leading-[0.96] tracking-[-0.04em] text-foreground">
          {t("title")}{" "}
          <span className="font-headline italic">{t("titleAccent")}</span>
        </h2>
        <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {t("subtitle")}
        </p>

        <div className="pointer-events-auto mt-2">
          <Link
            href={{ pathname: "/", hash: "contact" }}
            className="shimmer-on-hover group relative inline-flex h-12 items-center justify-center overflow-hidden rounded-full px-7 text-sm font-semibold text-white transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-[1px] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora-cyan/60"
          >
            <span
              aria-hidden
              className="absolute inset-0 rounded-full bg-[linear-gradient(45deg,var(--aurora-cyan),var(--aurora-violet))]"
            />
            <span
              aria-hidden
              className="-z-10 absolute inset-x-0 top-2 h-full rounded-full bg-[linear-gradient(45deg,var(--aurora-cyan),var(--aurora-violet))] opacity-50 blur-[14px] transition-opacity duration-500 group-hover:opacity-80"
            />
            <span className="relative">{t("cta")}</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
